import { idbGet, idbPut, STORES } from './database';
import type { OptimizationSettings } from '../pipeline/settings';
import { buildRecommendedSettings } from '../pipeline/settings';
import { DEFAULT_MODEL } from '../api/pricing';

const KEY_OPTIMIZATION = 'optimizationSettings';
const KEY_API_KEY = 'anthropicApiKey';
const KEY_TEMPLATES = 'taskTemplates';
const KEY_PATH_ALIASES = 'pathAliases';
const KEY_SELECTED_MODEL = 'selectedModel';
const KEY_CONTEXTTREES = 'contextTreesSettings';

export const DEFAULT_TEMPLATES: Record<string, string> = {
  '/explain': 'Explain what this code does and why, concisely.',
  '/refactor': 'Refactor this for clarity and simplicity without changing behavior.',
  '/tests': 'Write unit tests covering the main behavior and edge cases.',
  '/fix': 'Find and fix the bug. State the root cause in one line.',
  '/review': 'Review this for correctness, edge cases, and simplification opportunities.',
};

export async function getOptimizationSettings(): Promise<OptimizationSettings> {
  const stored = await idbGet<OptimizationSettings>(STORES.settings, KEY_OPTIMIZATION);
  return stored ?? buildRecommendedSettings();
}

export async function saveOptimizationSettings(settings: OptimizationSettings): Promise<void> {
  await idbPut(STORES.settings, settings, KEY_OPTIMIZATION);
}

export async function resetOptimizationSettingsToRecommended(): Promise<OptimizationSettings> {
  const recommended = buildRecommendedSettings();
  await saveOptimizationSettings(recommended);
  return recommended;
}

export async function getApiKey(): Promise<string> {
  return (await idbGet<string>(STORES.settings, KEY_API_KEY)) ?? '';
}

export async function saveApiKey(key: string): Promise<void> {
  await idbPut(STORES.settings, key, KEY_API_KEY);
}

export async function getTaskTemplates(): Promise<Record<string, string>> {
  return (await idbGet<Record<string, string>>(STORES.settings, KEY_TEMPLATES)) ?? { ...DEFAULT_TEMPLATES };
}

export async function saveTaskTemplates(templates: Record<string, string>): Promise<void> {
  await idbPut(STORES.settings, templates, KEY_TEMPLATES);
}

export async function getPathAliases(): Promise<Record<string, string>> {
  return (await idbGet<Record<string, string>>(STORES.settings, KEY_PATH_ALIASES)) ?? {};
}

export async function savePathAliases(aliases: Record<string, string>): Promise<void> {
  await idbPut(STORES.settings, aliases, KEY_PATH_ALIASES);
}

export async function getSelectedModel(): Promise<string> {
  return (await idbGet<string>(STORES.settings, KEY_SELECTED_MODEL)) ?? DEFAULT_MODEL;
}

export async function saveSelectedModel(model: string): Promise<void> {
  await idbPut(STORES.settings, model, KEY_SELECTED_MODEL);
}

export interface ContextTreesSettings {
  enabled: boolean;
  reviewMode: { designMap: 'auto' | 'manual'; agentMemory: 'auto' | 'manual' };
  updateAuthoring: 'llm' | 'heuristic';
}

/**
 * Off by default on purpose: ContextTrees adds a system-context block per request (and, with
 * `updateAuthoring: 'llm'`, an extra call to author reconciliation), which cuts against this app's
 * entire reason to exist. It has to be an explicit opt-in, never silently on. Review defaults to
 * manual for the same reason — nothing gets committed to the design map without the user seeing it.
 */
export const DEFAULT_CONTEXTTREES_SETTINGS: ContextTreesSettings = {
  enabled: false,
  reviewMode: { designMap: 'manual', agentMemory: 'manual' },
  updateAuthoring: 'heuristic',
};

export async function getContextTreesSettings(): Promise<ContextTreesSettings> {
  const stored = await idbGet<ContextTreesSettings>(STORES.settings, KEY_CONTEXTTREES);
  return stored ?? { ...DEFAULT_CONTEXTTREES_SETTINGS, reviewMode: { ...DEFAULT_CONTEXTTREES_SETTINGS.reviewMode } };
}

export async function saveContextTreesSettings(settings: ContextTreesSettings): Promise<void> {
  await idbPut(STORES.settings, settings, KEY_CONTEXTTREES);
}
